import './MetodiPagamento.css';
import '../LoghiPagamento.css';
import React, { useEffect, useRef, useState } from 'react';
import DialogMsg from '../DialogMsg';
import Loader from 'react-loader-spinner';
import API from '../../library/ApiManager';
import Fnc from '../../library/Functions';
import { getDemetraPaymentMethodPresentation } from './DemetraPaymentMethodUtils';

const MAX_PAYMENT_METHOD_ID_LENGTH = 255;
const DEMETRA_PAYMENT_HOSTS = ['api-stg.demetra.one', 'api.demetra.one'];

function DemetraMetodiPagamento(props) {
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentLink, setPaymentLink] = useState(null);
    const [dialog, setDialog] = useState(null);
    const iframeRef = useRef(null);
    const terminalResultRef = useRef(false);

    useEffect(() => {
        if (!props.invoiceData) {
            setLoading(false);
            return;
        }

        loadPaymentMethods();

    }, [props.invoiceData]);

    useEffect(() => {
        if (!paymentLink) return undefined;

        let expectedOrigin;

        try {
            const paymentUrl = new URL(paymentLink);

            if (!isValidPaymentLink(paymentUrl)) {
                throw new Error('Invalid origin');
            }

            expectedOrigin = paymentUrl.origin;

        } catch (e) {

            /*
             * Non viene loggato paymentLink perché potrebbe contenere
             * token o identificativi della sessione Demetra.
             */
            reportPaymentError(
                'DEMETRA_PAYMENT_LINK_INVALID',
                'paymentLinkValidation',
                'Payment link Demetra non valido o origin non utilizzabile.'
                    + getExceptionDetails(e),
                Fnc.ExcCatgory.BRIDGE.val,
                'Impossibile aprire il pagamento',
                'Il collegamento di pagamento non è valido.'
            );

            return undefined;
        }

        terminalResultRef.current = false;

        const onPaymentMessage = (event) => {

            /*
             * Ignoriamo:
             * - messaggi dopo un risultato terminale già gestito
             * - messaggi provenienti da un origin diverso
             * - messaggi non provenienti dall'iframe Demetra
             */
            if (terminalResultRef.current
                    || event.origin !== expectedOrigin
                    || !iframeRef.current
                    || event.source !== iframeRef.current.contentWindow) {
                return;
            }

            const data = parseMessageData(event.data);

            /*
             * Demetra ha confermato che l'unico messaggio che
             * l'e-commerce deve gestire è "payment-result".
             *
             * "paypal-payment-result" è interno a Demetra e deve
             * quindi essere ignorato.
             *
             * Questo return NON interrompe il pagamento:
             * termina solamente l'elaborazione di questo specifico
             * postMessage. Eventuali messaggi successivi continueranno
             * ad essere ricevuti dal listener.
             */
            if (!data || data.type !== 'payment-result') {
                return;
            }

            /*
             * Esito positivo valido.
             */
            if (data.status === 'CONFIRMED'
                    && isValidPaymentMethodId(data.demetraToken)) {

                terminalResultRef.current = true;
                setPaymentLink(null);

                submitOrder(data.demetraToken);

                /*
                 * Necessario per non proseguire nel ramo KO sotto.
                 */
                return;
            }

            /*
             * Da questo punto sappiamo che abbiamo ricevuto un
             * payment-result destinato all'e-commerce, ma non possiamo
             * proseguire con l'ordine.
             */
            terminalResultRef.current = true;
            setPaymentLink(null);

            /*
             * Caso molto importante:
             *
             * Demetra dichiara CONFIRMED ma il token necessario
             * all'e-commerce manca oppure non è valido.
             *
             * Lo distinguiamo da un vero KO del pagamento.
             */
            if (data.status === 'CONFIRMED') {

                reportPaymentError(
                    'DEMETRA_RESULT_CONFIRMED_INVALID_TOKEN',
                    'onPaymentMessage',
                    'Ricevuto payment-result CONFIRMED ma demetraToken non utilizzabile.'
                        + getPaymentResultDetails(data),
                    Fnc.ExcCatgory.BRIDGE.val,
                    'Il pagamento non è riuscito',
                    'Non è stato possibile completare il metodo di pagamento.'
                );

                return;
            }

            /*
             * Payment-result ricevuto correttamente ma con uno stato
             * diverso da CONFIRMED.
             *
             * Non assumiamo uno schema KO specifico di Demetra:
             * leggiamo solamente status, già previsto dal contratto
             * utilizzato da questo componente.
             */
            reportPaymentError(
                'DEMETRA_RESULT_NOT_CONFIRMED',
                'onPaymentMessage',
                'Il provider di pagamento ha restituito un payment-result non CONFIRMED.'
                    + getPaymentResultDetails(data),
                Fnc.ExcCatgory.PAY_METHOD.val,
                'Il pagamento non è riuscito',
                'Non è stato possibile completare il metodo di pagamento.'
            );
        };

        window.addEventListener('message', onPaymentMessage);

        return () => window.removeEventListener('message', onPaymentMessage);

    }, [paymentLink]);

    function loadPaymentMethods() {
        setLoading(true);

        API.getDemetraPaymentMethods((data) => {

            if (isOk(data)) {

                const methods = Array.isArray(data.paymentMethods)
                    ? data.paymentMethods
                    : [];

                setPaymentMethods(methods);

                if (methods.length === 0) {
                    openPaymentSession();
                    return;
                }

            } else {

                setPaymentMethods([]);

                reportPaymentError(
                    'DEMETRA_GET_PAYMENT_METHODS_KO',
                    'loadPaymentMethods',
                    'getDemetraPaymentMethods ha restituito KO.'
                        + getApiErrorDetails(data),
                    Fnc.ExcCatgory.PAY_METHOD.val,
                    'Impossibile recuperare i metodi di pagamento',
                    'Riprova più tardi.'
                );
            }

            setLoading(false);
        });
    }

    function openPaymentSession() {
        setLoading(true);

        API.openDemetraPaymentSession((data) => {

            if (isOk(data)
                    && typeof data.paymentLink === 'string'
                    && isValidPaymentLink(data.paymentLink)) {

                setPaymentLink(data.paymentLink);

            } else {

                /*
                 * Distinguiamo:
                 *
                 * 1. API KO
                 * 2. API OK ma contratto incompleto, cioè paymentLink
                 *    mancante/non valido.
                 */
                if (isOk(data)) {

                    reportPaymentError(
                        'DEMETRA_OPEN_SESSION_MISSING_LINK',
                        'openPaymentSession',
                        'openDemetraPaymentSession ha restituito OK ma paymentLink è assente o non valido.',
                        Fnc.ExcCatgory.BRIDGE.val,
                        'Impossibile aprire il pagamento',
                        'Riprova più tardi.'
                    );

                } else {

                    reportPaymentError(
                        'DEMETRA_OPEN_SESSION_KO',
                        'openPaymentSession',
                        'openDemetraPaymentSession ha restituito KO.'
                            + getApiErrorDetails(data),
                        Fnc.ExcCatgory.PAY_METHOD.val,
                        'Impossibile aprire il pagamento',
                        'Riprova più tardi.'
                    );
                }
            }

            setLoading(false);
        });
    }

    function submitOrder(paymentMethodId) {

        if (!isValidPaymentMethodId(paymentMethodId)) {

            /*
             * Non logghiamo mai il valore del paymentMethodId.
             * Registriamo soltanto perché è stato considerato invalido.
             */
            reportPaymentError(
                'DEMETRA_INVALID_PAYMENT_METHOD_ID',
                'submitOrder',
                'paymentMethodId assente o non valido.'
                    + getPaymentMethodValidationDetails(paymentMethodId),
                Fnc.ExcCatgory.PAY_METHOD.val,
                'Metodo di pagamento non valido',
                'Seleziona o crea un metodo di pagamento valido.'
            );

            return;
        }

        setCheckoutLoading(true);

        /*
         * Manteniamo esattamente la logica esistente.
         *
         * Questa classe riceve solamente la callback positiva di
         * billingPreview. Eventuali errori interni di billingPreview
         * devono essere propagati da ApiManager per poter essere
         * tracciati qui.
         */
        API.billingPreview(
            () => submitDemetraCheckout(paymentMethodId)
        );
    }

    function submitDemetraCheckout(paymentMethodId) {

        API.demetraCheckoutDirect(
            Fnc.jsonSerialize(props.invoiceData),
            paymentMethodId,
            (data) => {

                setCheckoutLoading(false);

                if (isOk(data)) {

                    props.handleNext(
                        new Fnc.Ret(Fnc.EnmPay.OKDIRECT, data)
                    );

                } else {

                    reportPaymentError(
                        'DEMETRA_CHECKOUT_KO',
                        'submitDemetraCheckout',
                        'demetraCheckoutDirect ha restituito KO.'
                            + getApiErrorDetails(data),
                        Fnc.ExcCatgory.ORDER_CONFIRM.val,
                        'Ordine non completato',
                        'Non è stato possibile completare l’ordine.'
                    );
                }
            }
        );
    }

    function returnToPaymentMethods() {

        /*
         * Demetra owns the session lifecycle:
         * no local cleanup request is made.
         */
        terminalResultRef.current = true;
        setDialog(null);
        setCheckoutLoading(false);
        setPaymentLink(null);
    }

    /*
     * Punto unico per tutti gli errori bloccanti che vengono
     * mostrati all'utente.
     *
     * Ogni dialog di errore prodotto da questa classe passa prima
     * da Fnc.gaException.
     */
    function reportPaymentError(
        errorCode,
        funct,
        desc,
        category,
        title,
        message,
        handleOK
    ) {

        /*
         * Aggiungiamo un minimo contesto utile a ricostruire
         * il punto del flusso, senza inserire dati personali
         * o dati sensibili di pagamento.
         *
         * Il sessionId NON serve aggiungerlo qui:
         * API.logException lo invia già separatamente al backend
         * tramite Fnc.getSessionId().
         */
        const context =
            ' | errorCode=' + errorCode
            + ' | hasInvoiceData=' + String(!!props.invoiceData)
            + ' | validBasket=' + String(!!props.validBasket)
            + ' | paymentMethodsCount=' + paymentMethods.length
            + ' | hasPaymentLink=' + String(!!paymentLink);

        const fullDescription =
            errorCode + ' | ' + desc + context;

        try {

            Fnc.gaException(
                '[DemetraMetodiPagamento@' + funct + ']',
                fullDescription,
                true,
                category
            );

        } catch (e) {

            /*
             * Un problema nel sistema di logging non deve mai
             * cambiare il comportamento del pagamento o impedire
             * la visualizzazione del dialog.
             */
            try {

                Fnc.log(
                    'Errore durante gaException in DemetraMetodiPagamento: '
                        + (e && e.stack ? e.stack : String(e)),
                    Fnc.LogLvl.ERROR
                );

            } catch (logError) {
                /*
                 * Non interferire con il flusso applicativo.
                 */
            }
        }

        showError(title, message, handleOK);
    }

    function showError(title, message, handleOK) {

        setDialog(
            new Fnc.DlgConf(
                title,
                message,
                false,
                'Chiudi',
                '',
                handleOK
            )
        );
    }

    return (
        <>
            <div className="iol-checkout-left-part col-xl-7">

                {!paymentLink &&
                    <section
                        className="demetra-payment-methods"
                        aria-busy={loading}
                    >

                        <div className="demetra-payment-methods-heading">

                            <p className="heading-3">
                                Scegli come pagare
                            </p>

                            <p className="caption">
                                Seleziona un metodo salvato oppure aggiungine uno nuovo.
                            </p>

                        </div>

                        {loading &&
                            <div className="demetra-payment-method-loader">

                                <Loader
                                    visible
                                    type="TailSpin"
                                    color={Fnc.LoadingInp.COLOR}
                                    height="40px"
                                    width="40px"
                                    timeout={Fnc.LoadingInp.TIMEOUT}
                                />

                            </div>
                        }

                        {!loading &&
                            <>
                                <div className="demetra-payment-method-list">

                                    {paymentMethods.map((method) => {

                                        const presentation =
                                            getDemetraPaymentMethodPresentation(method);

                                        return (
                                            <article
                                                className={
                                                    `demetra-payment-method-card demetra-payment-method-card--${presentation.kind}`
                                                }
                                                key={method.paymentMethodId}
                                            >

                                                <div
                                                    className="demetra-payment-method-card__logo"
                                                    aria-hidden="true"
                                                >

                                                    {presentation.logoClass
                                                        ? <span className={presentation.logoClass} />
                                                        : <PaymentMethodIcon kind={presentation.kind} />
                                                    }

                                                </div>

                                                <div className="demetra-payment-method-card__details">

                                                    <div className="iol-activity-title semibold">
                                                        {presentation.title}
                                                    </div>

                                                    {presentation.reference &&
                                                        <div className="caption">
                                                            {presentation.reference}
                                                        </div>
                                                    }

                                                </div>

                                                <button
                                                    className="fucsia-button semibold demetra-payment-method-card__action"
                                                    type="button"
                                                    disabled={
                                                        checkoutLoading
                                                        || !props.validBasket
                                                        || !isValidPaymentMethodId(method.paymentMethodId)
                                                    }
                                                    onClick={() =>
                                                        submitOrder(method.paymentMethodId)
                                                    }
                                                >
                                                    Usa questo
                                                </button>

                                            </article>
                                        );
                                    })}

                                </div>

                                <button
                                    className="demetra-add-payment-method semibold"
                                    type="button"
                                    disabled={
                                        checkoutLoading
                                        || !props.validBasket
                                    }
                                    onClick={openPaymentSession}
                                >

                                    <span aria-hidden="true">
                                        +
                                    </span>

                                    Aggiungi un nuovo metodo

                                </button>
                            </>
                        }

                    </section>
                }

                {paymentLink &&
                    <div className="iol-checkout-left demetra-payment-session">

                        <iframe
                            ref={iframeRef}
                            title="Pagamento sicuro Demetra"
                            src={paymentLink}
                            className="demetra-payment-frame"
                            scrolling="no"
                        />

                        {paymentMethods.length > 0 &&
                            <button
                                className="fucsia-button semibold checkout-primary-action demetra-payment-session__back"
                                type="button"
                                onClick={returnToPaymentMethods}
                            >

                                <svg
                                    className="checkout-primary-action__arrow demetra-payment-session__back-icon"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path d="m15 5-7 7 7 7" />
                                </svg>

                                <span>
                                    Torna ai metodi di pagamento
                                </span>

                                <span
                                    className="demetra-payment-session__back-spacer"
                                    aria-hidden="true"
                                />

                            </button>
                        }

                    </div>
                }

            </div>

            {checkoutLoading &&
                <div
                    className="demetra-checkout-overlay"
                    role="status"
                    aria-live="polite"
                >

                    <div className="demetra-checkout-overlay-card">

                        <Loader
                            visible
                            type="TailSpin"
                            color={Fnc.LoadingInp.COLOR}
                            height="64px"
                            width="64px"
                        />

                        <p className="heading-4 semibold">
                            Stiamo completando il tuo ordine
                        </p>

                        <p className="caption">
                            Attendi qualche istante, non chiudere questa pagina.
                        </p>

                    </div>

                </div>
            }

            {dialog &&
                <DialogMsg conf={dialog} />
            }

        </>
    );
}


/*
 * Estrae soltanto informazioni diagnostiche utili dalla risposta API.
 *
 * Evitiamo volutamente JSON.stringify(data), perché le response
 * del flusso di pagamento potrebbero in futuro contenere token,
 * URL di sessione o altri dati che non vogliamo scrivere nei log.
 */
function getApiErrorDetails(data) {

    if (!data) {
        return ' response=assente';
    }

    const details = [];

    if (!data.response) {

        details.push('response=missing');

    } else {

        if (data.response.code !== undefined
                && data.response.code !== null) {

            details.push(
                'code=' + safeLogValue(data.response.code)
            );
        }

        if (typeof data.response.message === 'string'
                && data.response.message.length > 0) {

            details.push(
                'message=' + data.response.message
            );
        }

        if (typeof data.response.description === 'string'
                && data.response.description.length > 0) {

            details.push(
                'description=' + data.response.description
            );
        }
    }

    /*
     * Alcune API potrebbero avere un message al primo livello.
     */
    if (typeof data.message === 'string'
            && data.message.length > 0) {

        details.push(
            'message=' + data.message
        );
    }

    if (details.length === 0) {
        return ' risposta presente senza dettagli diagnostici';
    }

    return ' | ' + details.join(' | ');
}


/*
 * Dettaglio diagnostico del payment-result.
 *
 * NON inseriamo mai il valore reale del demetraToken.
 */
function getPaymentResultDetails(data) {

    const details = [];

    if (!data) {
        return ' | payment-result assente';
    }

    details.push(
        'status=' + safeLogValue(data.status)
    );

    details.push(
        'demetraToken=' + getPaymentMethodValidationStatus(data.demetraToken)
    );

    return ' | ' + details.join(' | ');
}


/*
 * Spiega perché un paymentMethodId / demetraToken non è valido
 * senza loggarne mai il valore.
 */
function getPaymentMethodValidationDetails(value) {

    return ' | validation='
        + getPaymentMethodValidationStatus(value);
}


function getPaymentMethodValidationStatus(value) {

    if (value === null) {
        return 'null';
    }

    if (value === undefined) {
        return 'missing';
    }

    if (typeof value !== 'string') {
        return 'invalid-type(' + typeof value + ')';
    }

    if (value.trim().length === 0) {
        return 'empty';
    }

    if (value.length > MAX_PAYMENT_METHOD_ID_LENGTH) {
        return 'too-long(length='
            + value.length
            + ',max='
            + MAX_PAYMENT_METHOD_ID_LENGTH
            + ')';
    }

    if (!/^[\x21-\x7e]+$/.test(value)) {
        return 'invalid-format';
    }

    return 'valid';
}


function getExceptionDetails(error) {

    if (!error) {
        return '';
    }

    if (typeof error.message === 'string'
            && error.message.length > 0) {

        return ' | exception=' + error.message;
    }

    return '';
}


function safeLogValue(value) {

    if (value === null) {
        return 'null';
    }

    if (value === undefined) {
        return 'undefined';
    }

    return String(value);
}


function isOk(data) {

    return data
        && data.response
        && data.response.code === 'OK';
}


function isValidPaymentMethodId(value) {

    return typeof value === 'string'
        && value.length > 0
        && value.length <= MAX_PAYMENT_METHOD_ID_LENGTH
        && /^[\x21-\x7e]+$/.test(value);
}


function isValidPaymentLink(value) {

    try {
        const url = value instanceof URL ? value : new URL(value);
        return url.protocol === 'https:'
            && DEMETRA_PAYMENT_HOSTS.includes(url.hostname.toLowerCase())
            && !url.username
            && !url.password
            && (!url.port || url.port === '443');
    } catch (error) {
        return false;
    }
}


function parseMessageData(value) {

    if (value && typeof value === 'object') {
        return value;
    }

    if (typeof value !== 'string') {
        return null;
    }

    try {

        const parsed = JSON.parse(value);

        return parsed && typeof parsed === 'object'
            ? parsed
            : null;

    } catch (e) {

        return null;
    }
}


function PaymentMethodIcon(props) {

    if (props.kind === 'card') {

        return (
            <svg
                className="demetra-payment-method-card__generic-icon"
                viewBox="0 0 48 32"
                focusable="false"
            >

                <rect
                    x="1"
                    y="1"
                    width="46"
                    height="30"
                    rx="4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                />

                <path
                    d="M2 10h44"
                    stroke="currentColor"
                    strokeWidth="2"
                />

                <rect
                    x="7"
                    y="17"
                    width="13"
                    height="3"
                    rx="1.5"
                    fill="currentColor"
                />

                <rect
                    x="7"
                    y="23"
                    width="20"
                    height="2"
                    rx="1"
                    fill="currentColor"
                    opacity=".45"
                />

            </svg>
        );
    }

    return (
        <svg
            className="demetra-payment-method-card__generic-icon"
            viewBox="0 0 48 32"
            focusable="false"
        >

            <path
                d="M5 10.5h34.5c2.5 0 4.5 2 4.5 4.5v10.5c0 2.5-2 4.5-4.5 4.5H8c-2.2 0-4-1.8-4-4V11.5c0-3.6 2.9-6.5 6.5-6.5H36"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />

            <path
                d="M31 19h13v6H31c-1.7 0-3-1.3-3-3s1.3-3 3-3Z"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />

            <circle
                cx="34"
                cy="22"
                r="1.5"
                fill="currentColor"
            />

        </svg>
    );
}


export default DemetraMetodiPagamento;
